import { useCallback, useEffect, useRef, useState } from 'react';

import { ConsultationRecording } from '@/entities/transcriptor';

import { UseRecordingAudioReturn } from '../config';
import { isSameAudioObject } from '../lib';

export const useRecordingAudio = (
  recording: ConsultationRecording,
): UseRecordingAudioReturn => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const pendingSeekRef = useRef<number | null>(null);
  const [renderedUrl, setRenderedUrl] = useState<string | null>(recording.url);

  const playSafely = useCallback(() => {
    audioRef.current?.play().catch(() => undefined);
  }, []);

  useEffect(() => {
    setRenderedUrl((previous) => {
      if (recording.url === null) {
        return null;
      }

      // Keeping the previous reference leaves the <audio> src untouched, so a
      // re-presigned URL never restarts playback.
      if (previous !== null && isSameAudioObject(previous, recording.url)) {
        return previous;
      }

      return recording.url;
    });
  }, [recording.url]);

  const seekTo = useCallback(
    (localSec: number) => {
      if (!audioRef.current) {
        return;
      }

      audioRef.current.currentTime = localSec;
      playSafely();
    },
    [playSafely],
  );

  const handleLoadedMetadata = useCallback(() => {
    if (pendingSeekRef.current === null || !audioRef.current) {
      return;
    }

    audioRef.current.currentTime = pendingSeekRef.current;
    pendingSeekRef.current = null;
    playSafely();
  }, [playSafely]);

  const handleError = useCallback(() => {
    if (recording.url === null || recording.url === renderedUrl) {
      return;
    }

    pendingSeekRef.current = audioRef.current?.currentTime ?? 0;
    setRenderedUrl(recording.url);
  }, [recording.url, renderedUrl]);

  return {
    audioRef,
    src: renderedUrl ?? undefined,
    seekTo,
    handleLoadedMetadata,
    handleError,
  };
};
